import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Grid,
    LinearProgress,
    Paper,
    Stack,
    Typography
} from '@mui/material';
import {
    Favorite as FavoriteIcon,
    Login as LoginIcon,
    Psychology as PsychologyIcon,
    Verified as VerifiedIcon
} from '@mui/icons-material';

import { apiClient } from '../utils/api';

interface AvatarShareReport {
    user: {
        id: string;
        display_name: string;
        username: string;
        age?: number | null;
        location?: string | null;
        bio?: string | null;
        primary_photo_url?: string | null;
        is_verified: boolean;
    };
    real_info: Record<string, string | number>;
    avatar_report: Record<string, any>;
    personality_report: Record<string, any>;
    share_meta: Record<string, any>;
}

const labelMap: Record<string, string> = {
    real_name: 'Real name',
    age: 'Age',
    occupation: 'Occupation',
    education: 'Education',
    hometown: 'Hometown',
    relationship_goal: 'Relationship goal',
    lifestyle: 'Lifestyle',
    interests: 'Interests',
    values: 'Values',
    contact_preference: 'Contact preference'
};

const scoreLabel = (score: unknown) => `${Math.round(Number(score || 0) * 100)}%`;

const AvatarShareReportPage: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [report, setReport] = useState<AvatarShareReport | null>(null);
    const [loading, setLoading] = useState(true);
    const [liking, setLiking] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const realInfoEntries = useMemo(() => Object.entries(report?.real_info || {}), [report]);
    const personality = report?.personality_report || {};
    const avatarReport = report?.avatar_report || {};

    useEffect(() => {
        loadReport();
    }, [userId]);

    const loadReport = async () => {
        if (!userId) return;
        try {
            setLoading(true);
            setError(null);
            const data = await apiClient.get<AvatarShareReport>(`/api/v1/avatar/share/${userId}/report`, { skipAuth: true });
            setReport(data);
        } catch (err: any) {
            setError(err.message || 'Failed to load avatar report');
        } finally {
            setLoading(false);
        }
    };

    const likeUser = async () => {
        if (!report) return;
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/auth/login', { state: { from: location } });
            return;
        }

        try {
            setLiking(true);
            setMessage(null);
            setError(null);
            const result = await apiClient.post<{ message: string; is_mutual: boolean; match_id: string }>(`/api/v1/matches/like/${report.user.id}`);
            setMessage(result.is_mutual ? 'It is a mutual match. You can continue from Matches.' : result.message);
        } catch (err: any) {
            setError(err.message || 'Failed to like this user');
        } finally {
            setLiking(false);
        }
    };

    if (loading) {
        return (
            <Container maxWidth="md">
                <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error && !report) {
        return (
            <Container maxWidth="md">
                <Box py={8}>
                    <Alert severity="error">{error}</Alert>
                </Box>
            </Container>
        );
    }

    if (!report) return null;

    return (
        <Container maxWidth="lg">
            <Box py={4}>
                <Paper sx={{ p: { xs: 3, md: 4 }, mb: 3 }}>
                    <Grid container spacing={3} alignItems="center">
                        <Grid item>
                            <Avatar src={report.user.primary_photo_url || undefined} sx={{ width: 96, height: 96 }}>
                                {report.user.display_name.slice(0, 1)}
                            </Avatar>
                        </Grid>
                        <Grid item xs>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Typography variant="h4" component="h1">
                                    {report.user.display_name}
                                </Typography>
                                {report.user.is_verified && <Chip icon={<VerifiedIcon />} label="Verified" color="success" size="small" />}
                            </Stack>
                            <Typography color="text.secondary">
                                {[report.user.age ? `${report.user.age}` : null, report.user.location].filter(Boolean).join(' · ')}
                            </Typography>
                            {report.user.bio && (
                                <Typography sx={{ mt: 1 }}>
                                    {report.user.bio}
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={12} md="auto">
                            <Button variant="contained" size="large" startIcon={liking ? <CircularProgress size={18} /> : <FavoriteIcon />} onClick={likeUser} disabled={liking}>
                                {liking ? 'Liking...' : 'Like'}
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>

                {message && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setMessage(null)}>{message}</Alert>}
                {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                Real Information
                            </Typography>
                            {realInfoEntries.length === 0 ? (
                                <Typography color="text.secondary">No real information has been added yet.</Typography>
                            ) : (
                                <Stack spacing={1.5}>
                                    {realInfoEntries.map(([key, value]) => (
                                        <Box key={key}>
                                            <Typography variant="caption" color="text.secondary">
                                                {labelMap[key] || key}
                                            </Typography>
                                            <Typography>{value}</Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            )}
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, height: '100%' }}>
                            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                                <PsychologyIcon color="primary" />
                                <Typography variant="h6">Avatar Report</Typography>
                            </Stack>
                            <Typography variant="subtitle1">{avatarReport.name || 'AI Avatar'}</Typography>
                            <Typography color="text.secondary" sx={{ mb: 2 }}>{avatarReport.description || avatarReport.summary}</Typography>
                            {['completeness_score', 'authenticity_score', 'consistency_score'].map(score => (
                                <Box key={score} mb={1.5}>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="body2">{score.replace('_score', '').replace('_', ' ')}</Typography>
                                        <Typography variant="body2">{scoreLabel(avatarReport[score])}</Typography>
                                    </Box>
                                    <LinearProgress variant="determinate" value={Number(avatarReport[score] || 0) * 100} />
                                </Box>
                            ))}
                            {Array.isArray(avatarReport.memory_facts) && avatarReport.memory_facts.length > 0 && (
                                <Box mt={2}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Learned from conversations
                                    </Typography>
                                    <Stack spacing={1}>
                                        {avatarReport.memory_facts.map((fact: any) => (
                                            <Box key={`${fact.name}-${fact.value}`}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {fact.name}
                                                </Typography>
                                                <Typography variant="body2">{fact.value}</Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    <Grid item xs={12}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Personality Report
                            </Typography>
                            {personality.status === 'missing' ? (
                                <Typography color="text.secondary">{personality.summary}</Typography>
                            ) : (
                                <Grid container spacing={2}>
                                    {Object.entries(personality.big_five || {}).map(([trait, value]) => (
                                        <Grid item xs={12} sm={6} md={4} key={trait}>
                                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{trait}</Typography>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <LinearProgress variant="determinate" value={Number(value || 0) * 100} sx={{ flexGrow: 1 }} />
                                                <Typography variant="body2">{scoreLabel(value)}</Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="caption" color="text.secondary">Communication style</Typography>
                                        <Typography>{personality.communication_style || 'Not specified'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="caption" color="text.secondary">Conflict style</Typography>
                                        <Typography>{personality.conflict_resolution_style || 'Not specified'}</Typography>
                                    </Grid>
                                </Grid>
                            )}
                        </Paper>
                    </Grid>
                </Grid>

                {!localStorage.getItem('token') && (
                    <Alert severity="info" icon={<LoginIcon />} sx={{ mt: 3 }}>
                        You can view this report without logging in. Log in only when you want to like this person.
                    </Alert>
                )}
            </Box>
        </Container>
    );
};

export default AvatarShareReportPage;
